import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ExternalLink, Phone, Mail, MapPin, UtensilsCrossed, 
  Sparkles, Anchor, ChefHat, Globe, Clock
} from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Default guest services data (fallback if no database entries)
const defaultGuestServices = [
  {
    category: 'Cottage Catering',
    icon: 'chef-hat',
    description: 'Let these local caterers prepare delicious meals so you can relax and enjoy your vacation.',
    services: [
      {
        name: 'BE Catering',
        description: 'If you are going on a vacation to rest, BE Catering can prepare meals for you to just heat and serve while you are there! Sit back and relax with a book and a glass of wine...dinner is done. We can even come up to your cottage and grill and cook for you, so you don\'t have to clean up. Make your next holiday a real holiday: have BE Catering help you out!',
        address: '188 Hunter Street West, Unit #6, Peterborough, ON K9H 2L2',
        phone: '(705) 750-1265',
        tollFree: '1-888-854-9988',
        email: 'info@becatering.com',
        website: 'http://www.becatering.com',
        logo: 'https://dev.kawartha-luxury-properties.com/properties/images/be_catering_logo.gif'
      },
      {
        name: 'Catering Plus',
        description: 'Catering Plus is Peterborough and area\'s choice for catering excellence and customer satisfaction. Our menu offers a wide variety of choices. We create tempting breakfast trays, satisfying lunches, and home cooked style dinners tailored to any business function or personal gathering.',
        address: 'Peterborough, ON',
        phone: '(705) 876-6399',
        email: 'info@cateringplus.org',
        website: 'http://www.cateringplus.org',
        logo: 'https://dev.kawartha-luxury-properties.com/properties/images/cateringplus.gif'
      },
      {
        name: "Franz's Butchershop & Catering",
        description: "Franz's meat market has been proudly serving Peterborough and the Kawarthas for over 18 years, and has consistently taken pride in assuring that its customers receive the finest expertly aged meat products. Franz Roessl opened his business in 1983 and the success and rapid growth of his clientele is true testament to the tradition and excellence of their products.",
        address: 'Peterborough, ON',
        phone: '(705) 742-8888',
        email: 'franz@franzs.com',
        website: 'http://www.franzs.com',
        logo: 'https://dev.kawartha-luxury-properties.com/properties/images/franz.gif'
      },
      {
        name: 'Old Bridge Inn',
        description: 'Award winning chef/owner Evan Podd creates a mouth-watering array of dishes in one of the area\'s finest kitchens. Whether you are planning a wedding for 100 or a family gathering for 20, the "OBI" offers custom catering packages for your event.',
        address: 'Young\'s Point, ON',
        phone: '(705) 652-8507',
        tollFree: '1-877-613-6693',
        email: 'info@theoldbridgeinn.com',
        website: 'http://www.theoldbridgeinn.com',
        logo: 'https://dev.kawartha-luxury-properties.com/properties/images/old_bridge_logo.jpg'
      },
      {
        name: 'Parkhill on Hunter',
        description: 'Serving the Peterborough area for more than 20 years, we continue to offer creative food, in a relaxed yet elegant atmosphere, with warm and caring service. Parkhill on Hunter provides the perfect setting for intimate dinners with friends and family, a casual drink at the bar, any special occasion or corporate function.',
        address: 'Peterborough, ON',
        phone: '(705) 743-8111',
        email: 'parkhillcafe@on.aibn.com',
        website: 'http://www.parkhillonhunter.com',
        logo: 'https://dev.kawartha-luxury-properties.com/properties/images/parkhill_logo.gif'
      }
    ]
  },
  {
    category: 'Ski/Fishing Boats & Seadoo Rentals',
    icon: 'anchor',
    description: 'Enjoy the lake with watercraft rentals from our trusted local partners.',
    services: [
      {
        name: 'Kawartha Fun On Water Rentals',
        description: 'There is no better way to have fun with your family and friends when you experience the excitement of a personal watercraft together. Located at Forest Glen Marina on Jack Lake.',
        address: 'Forest Glen Marina, Jack Lake',
        phone: '(705) 768-3592',
        email: 'ahalcovitch@funonwater.ca',
        website: 'http://www.funonwater.ca',
        logo: 'https://dev.kawartha-luxury-properties.com/images/FUNONWATER(1).jpg',
        rates: [
          { name: 'Half Day (4 hours)', price: '$200.00 + tax + gas' },
          { name: 'Full Day (8 hours)', price: '$300.00 + tax + gas' },
          { name: 'Weekly', price: '$1,500.00 + tax + gas' }
        ]
      }
    ]
  },
  {
    category: 'Mobile Spa Services',
    icon: 'sparkles',
    description: 'Pamper yourself with professional spa services that come to you.',
    services: [
      {
        name: 'Sunshrine Day Spa & Salon',
        description: 'Sunshrine is an award winning day spa and salon that offers a full-service hair salon, tanning, registered massage therapy and complete esthetic services. In addition to being a 7 time Award winning Spa & Salon, Sunshrine has been published nationally in many articles, including Modern Salon, Salon Today, Salon News, & the front cover of Kawartha Women\'s Magazine.',
        address: '21 Queen St., Lakefield, ON K0L 2H0',
        phone: '(705) 652-0155',
        email: 'info@sunshrine.com',
        website: 'http://www.sunshrine.com',
        logo: 'https://dev.kawartha-luxury-properties.com/properties/images/spa.jpg'
      }
    ]
  }
];

const iconMap = {
  'chef-hat': ChefHat,
  'anchor': Anchor,
  'sparkles': Sparkles,
  'utensils': UtensilsCrossed,
};

export default function LinksPage() {
  const [guestServices, setGuestServices] = useState(defaultGuestServices);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGuestServices = async () => {
      try {
        const res = await axios.get(`${API}/guest-services`);
        if (res.data && res.data.length > 0) {
          setGuestServices(res.data);
        }
      } catch (error) {
        // Use default data if API fails
        console.log('Using default guest services data');
      } finally {
        setLoading(false);
      }
    };
    fetchGuestServices();
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-[#1B4D3E]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-4">For Our Guests</p>
            <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">Guest Services</h1>
            <p className="text-white/80 text-lg">
              We've partnered with trusted local businesses to enhance your stay at Big Bear Estate. 
              From catering to watercraft rentals and spa services, these providers can help make your 
              vacation truly special.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Guest Services Sections */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          {guestServices.map((category, catIndex) => {
            const CategoryIcon = iconMap[category.icon] || UtensilsCrossed;
            
            return (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: catIndex * 0.1 }}
                className="mb-16"
              >
                {/* Category Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#1B4D3E] rounded-full flex items-center justify-center">
                    <CategoryIcon className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="font-serif text-2xl md:text-3xl text-[#1C1C1C]">
                      {category.category}
                    </h2>
                    {category.description && (
                      <p className="text-[#66605B] mt-1">{category.description}</p>
                    )}
                  </div>
                </div>

                {/* Services List */}
                <div className="space-y-6">
                  {category.services.map((service, svcIndex) => (
                    <motion.div
                      key={service.name}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: svcIndex * 0.1 }}
                      className="bg-white rounded-lg shadow-sm overflow-hidden border border-[#E2E0DC]"
                    >
                      <div className="p-6">
                        <div className="flex flex-col md:flex-row gap-6">
                          {/* Logo */}
                          {service.logo && (
                            <div className="flex-shrink-0">
                              <img 
                                src={getImageUrl(service.logo)} 
                                alt={service.name}
                                className="w-32 h-auto object-contain rounded"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          )}
                          
                          {/* Content */}
                          <div className="flex-1">
                            <h3 className="font-serif text-xl text-[#1B4D3E] mb-2">
                              {service.name}
                            </h3>
                            <p className="text-[#66605B] mb-4 leading-relaxed">
                              {service.description}
                            </p>

                            {/* Rates (if available) */}
                            {service.rates && service.rates.length > 0 && (
                              <div className="mb-4 p-4 bg-[#F0EBE5] rounded-lg">
                                <h4 className="font-semibold text-[#1C1C1C] mb-2 flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-[#1B4D3E]" />
                                  Rates
                                </h4>
                                <ul className="space-y-1">
                                  {service.rates.map((rate, rIdx) => (
                                    <li key={rIdx} className="flex justify-between text-sm">
                                      <span className="text-[#66605B]">{rate.name}</span>
                                      <span className="font-medium text-[#1C1C1C]">{rate.price}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Contact Info */}
                            <div className="flex flex-wrap gap-4 text-sm">
                              {service.address && (
                                <div className="flex items-start gap-2 text-[#66605B]">
                                  <MapPin className="w-4 h-4 text-[#1B4D3E] mt-0.5 flex-shrink-0" />
                                  <span>{service.address}</span>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap gap-4 mt-3">
                              {service.phone && (
                                <a 
                                  href={`tel:${service.phone.replace(/[^0-9]/g, '')}`}
                                  className="flex items-center gap-2 text-[#1B4D3E] hover:text-[#D4AF37] transition-colors"
                                >
                                  <Phone className="w-4 h-4" />
                                  <span className="font-medium">{service.phone}</span>
                                </a>
                              )}
                              {service.tollFree && (
                                <a 
                                  href={`tel:${service.tollFree.replace(/[^0-9]/g, '')}`}
                                  className="flex items-center gap-2 text-[#1B4D3E] hover:text-[#D4AF37] transition-colors"
                                >
                                  <Phone className="w-4 h-4" />
                                  <span className="font-medium">{service.tollFree} (Toll Free)</span>
                                </a>
                              )}
                              {service.email && (
                                <a 
                                  href={`mailto:${service.email}`}
                                  className="flex items-center gap-2 text-[#1B4D3E] hover:text-[#D4AF37] transition-colors"
                                >
                                  <Mail className="w-4 h-4" />
                                  <span>{service.email}</span>
                                </a>
                              )}
                              {service.website && (
                                <a 
                                  href={service.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-[#1B4D3E] hover:text-[#D4AF37] transition-colors"
                                >
                                  <Globe className="w-4 h-4" />
                                  <span>Website</span>
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-8 p-6 bg-[#F0EBE5] rounded-lg"
          >
            <p className="text-sm text-[#66605B] text-center">
              These services are provided by independent businesses and are not affiliated with Big Bear Estate 
              or Kawartha Luxury Properties. Please contact each provider directly to verify availability, 
              current pricing, and services offered. We recommend booking in advance during peak season.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
